import { useEffect } from 'react';
import { gql, useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { WebPushContentArgsInput, UpsertTemplateResult } from 'modules/graphql/types';
import { addNegativeToast } from 'modules/toastContainer';
import { isEmpty } from '@iterable/utils';
import noOp from 'utils/noop';
import { Config } from './types';

export type UpdateWebPushTemplateRequest = {
  cacheDataFeed: boolean;
  campaignId?: number;
  content: WebPushContentArgsInput;
  dateFeedIds: number[];
  mergeDataFeedContext: boolean;
  messageTypeId: number;
  name: string;
  referenceDataFeedsByAlias: boolean;
  templateId: number;
};

type UpdateWebPushTemplateResponse = {
  updateWebPushTemplate: UpsertTemplateResult;
};

export const UpdateWebPushTemplateMutation = gql`
  mutation UpdateWebPushTemplate(
    $cacheDataFeed: Boolean!
    $campaignId: Long
    $content: WebPushContentArgsInput!
    $dataFeedIds: [Long!]!
    $mergeDataFeedContext: Boolean!
    $messageTypeId: Long!
    $name: String!
    $referenceDataFeedsByAlias: Boolean!
    $templateId: Long!
  ) {
    updateWebPushTemplate(
      cacheDataFeed: $cacheDataFeed
      campaignId: $campaignId
      content: $content
      dataFeedIds: $dataFeedIds
      mergeDataFeedContext: $mergeDataFeedContext
      messageTypeId: $messageTypeId
      name: $name
      referenceDataFeedsByAlias: $referenceDataFeedsByAlias
      templateId: $templateId
    ) {
      failure {
        ... on InvalidTemplateContents {
          errors {
            ... on InvalidTemplateContent {
              reasons {
                ... on Unrenderable {
                  fieldName
                  msg
                }
                ... on MissingSnippets {
                  snippets
                }
                ... on InvalidSnippetParams {
                  snippetsWithParams
                }
              }
              content {
                ... on WebPushContentArgs {
                  locale {
                    name
                  }
                }
              }
            }
          }
        }
        ... on TemplateNotFound {
          templateId
        }
        __typename
      }
      templateId
    }
  }
`;

export const useUpdateWebPushTemplate = ({
  onComplete = noOp,
  onError = noOp,
  onSuccess = noOp,
}: Config) => {
  const dispatch = useDispatch();
  const [upsertWebPushTemplate, data] = useMutation<
    UpdateWebPushTemplateResponse,
    UpdateWebPushTemplateRequest
  >(UpdateWebPushTemplateMutation);
  const fieldErrors: Record<string, string> = {};

  useEffect(() => {
    if (!data.called) return;
    if (!data.loading && data.error) {
      dispatch(addNegativeToast('An unknown error occurred'));
    }

    if (!data.loading && data?.data?.updateWebPushTemplate) {
      const { templateId } = data.data.updateWebPushTemplate;
      if (templateId) {
        onSuccess(templateId);
      } else if (!isEmpty(fieldErrors)) {
        onError(fieldErrors);
      }
    }
    onComplete();
  }, [data.loading]);
  return [upsertWebPushTemplate, data] as const;
};
